/**
 * meshAnalyze.js — read a mesh file and measure the solid it encloses.
 *
 * The bridge between a `File` on the operator's disk and volumes.js, which is
 * pure and takes typed arrays. This is the only part of the volume path that
 * touches a loader, and it is deliberately the ONLY part: the maths, the welding
 * and the watertight verdict stay testable without a browser.
 *
 * ── Why this is its own module, and not an export from meshRenderer.js ──────
 *
 * The volume must be of **the same geometry the report renders**, or the number
 * on page 3 describes something other than the picture beside it. So the parsing
 * has to match the renderer's — and it does, because both use three's own
 * STLLoader and OBJLoader with the same options. What is NOT copied is any of
 * our own logic; there is none here beyond picking the loader.
 *
 * `meshRenderer.js` would have been the natural home (it already imports these
 * loaders), but it is a large, heavily hash-pinned module that another branch is
 * editing. A thin separate module reaches the same geometry without touching it,
 * and without an `extraViews`-style change that genuinely does need to go there.
 *
 * ⚠️ Polypaint is deliberately NOT parsed. `loadMeshes` has an opt-in ZBrush
 * polypaint path that splits an OBJ into per-group meshes; it changes the mesh
 * LIST, never a vertex position, so the enclosed volume is identical either way.
 * Staying on the default path keeps this module's geometry byte-identical to the
 * renderer's default and avoids depending on a flag that is off by default.
 */

import { analyzeGeometry } from './volumes.js'

let _loaders = null

/** three's loaders, imported once. Dynamic, so nothing loads three to run a unit test. */
async function loaders() {
  if (_loaders) return _loaders
  const [{ STLLoader }, { OBJLoader }] = await Promise.all([
    import('three/addons/loaders/STLLoader.js'),
    import('three/addons/loaders/OBJLoader.js'),
  ])
  _loaders = { STLLoader, OBJLoader }
  return _loaders
}

const extOf = (name) => String(name ?? '').toLowerCase().split('.').pop()

/**
 * Every triangle in the file, concatenated into one position/index pair.
 *
 * Concatenated rather than measured per mesh: an OBJ may carry several `g`
 * groups that together form ONE closed solid — the team's exports do — and
 * measuring each group alone would report several open shells instead of one
 * sealed volume. Indices are re-based per mesh so the concatenation is valid.
 */
function flatten(geometries) {
  const positions = []
  const index = []
  let base = 0
  for (const g of geometries) {
    const pos = g.getAttribute?.('position')
    if (!pos) continue
    const arr = pos.array
    for (let i = 0; i < arr.length; i++) positions.push(arr[i])
    const idx = g.getIndex?.()
    if (idx) {
      for (let i = 0; i < idx.count; i++) index.push(base + idx.getX(i))
    } else {
      // Non-indexed: the triangles are the vertex order. volumes.js accepts a
      // null index, but once concatenated with an indexed mesh the offsets
      // matter, so an explicit index is always built.
      for (let i = 0; i < pos.count; i++) index.push(base + i)
    }
    base += pos.count
  }
  return {
    positions: Float64Array.from(positions),
    index: Uint32Array.from(index),
  }
}

/**
 * Volume and watertight verdict for one mesh file.
 *
 * @param {ArrayBuffer|Uint8Array} bytes
 * @param {string} filename  used only to choose the loader and to name warnings
 * @returns {Promise<{cc:number, watertight:boolean, boundaryEdges:number,
 *   nonManifoldEdges:number, tris:number, verts:number, filename:string,
 *   meshes:number}>}
 * @throws {Error} when the file holds no triangles — loudly, because a silent
 *   zero would print "0.00cc" on a surgical document.
 */
export async function analyzeMeshBytes(bytes, filename) {
  const ext = extOf(filename)
  const buf = bytes instanceof ArrayBuffer
    ? bytes
    : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
  const { STLLoader, OBJLoader } = await loaders()

  const geometries = []
  if (ext === 'stl') {
    geometries.push(new STLLoader().parse(buf))
  } else if (ext === 'obj') {
    const obj = new OBJLoader().parse(new TextDecoder().decode(buf))
    obj.traverse((o) => { if (o.isMesh && o.geometry) geometries.push(o.geometry) })
  } else {
    throw new Error(`${filename}: only .stl and .obj can be measured, not ".${ext}"`)
  }

  const { positions, index } = flatten(geometries)
  if (index.length < 3) {
    throw new Error(`${filename}: no triangles found — cannot measure a volume`)
  }
  return {
    ...analyzeGeometry(positions, index),
    filename,
    meshes: geometries.length,
  }
}

/** Read a File/Blob and measure it. */
export async function analyzeMeshFile(file) {
  return analyzeMeshBytes(await file.arrayBuffer(), file.name)
}

/**
 * A cache key that changes when the file does.
 *
 * Same shape as the render cache's: name, size, mtime. Content is not hashed —
 * these files are 6-25 MB and hashing every one on every keystroke is what the
 * render cache already avoids.
 */
export const analyzeKey = (file) =>
  `${file?.name ?? ''}|${file?.size ?? 0}|${file?.lastModified ?? 0}`

/**
 * Measure every mesh a set of roles needs, keyed as draftReport expects.
 *
 * Returns `{ volumes, warnings, errors }`:
 *   volumes  — 'role/stage' → the analysis, ready for draftReport
 *   warnings — one per non-watertight mesh, already worded for the operator
 *   errors   — one per file that could not be read, naming the file
 *
 * A file that fails to read becomes an ERROR, never a missing entry: the recipe
 * would otherwise skip its page with "volumes not computed", which reads as a
 * case without volumes rather than a file that would not open.
 *
 * @param {Array} library   from buildLibrary(), entries carrying `handle`
 * @param {Set<string>|Array<string>} roles  which roles to measure
 */
export async function analyzeVolumeMeshes(library, roles, { cache = new Map() } = {}) {
  const want = roles instanceof Set ? roles : new Set(roles)
  const volumes = {}
  const warnings = []
  const errors = []
  for (const mesh of library) {
    if (!want.has(mesh.role) || !mesh.handle) continue
    const key = analyzeKey(mesh.handle)
    try {
      let result = cache.get(key)
      if (!result) {
        result = await analyzeMeshFile(mesh.handle)
        cache.set(key, result)
      }
      volumes[`${mesh.role}/${mesh.stage}`] = result
      if (result.watertight === false) {
        const { watertightWarning } = await import('./volumes.js')
        warnings.push(watertightWarning(mesh.name, result.cc))
      }
    } catch (e) {
      errors.push(`Could not read ${mesh.name} to measure its volume (${e?.message ?? e}). `
        + 'Check the file is fully downloaded, then re-pick the folder.')
    }
  }
  return { volumes, warnings, errors }
}

/** The roles whose volumes this report quotes. */
export const ORBITAL_VOLUME_ROLES = ['orbital_volume_left', 'orbital_volume_right']
